import React from "react";
import Container from "Components/Container/Container";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  FacebookLogo,
  InstagramLogo,
  LinkedinLogo,
  ArrowRight,
} from "@phosphor-icons/react";
import BASE_URL from "Utilities/BASE_URL";

/**
 * Instructor block.
 *
 * Desktop (lg+): UNCHANGED — same horizontal layout with a 72x72 circular
 *                 avatar, name, bio and the pink pill "View Profile" button.
 *                 All `lg:*` classes preserved.
 * Mobile:         redesigned as a dark card (#1f1f22 / border-white/[0.08])
 *                 matching HomeBanner / HeroActionCard. Avatar downsized to
 *                 80px, added social-icon row if the data includes any of
 *                 facebook/instagram/linkedin, and the CTA is full-width.
 */
const Instructor = ({ data }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const imageUrl = data?.profileImage
    ? `${BASE_URL.replace(/\/api$/, "")}/${data.profileImage}`
    : null;

  const socials = [
    data?.facebook && { href: data.facebook, Icon: FacebookLogo, label: "Facebook" },
    data?.instagram && { href: data.instagram, Icon: InstagramLogo, label: "Instagram" },
    data?.linkedin && { href: data.linkedin, Icon: LinkedinLogo, label: "LinkedIn" },
  ].filter(Boolean);

  return (
    <section className="pt-10 sm:pt-14 lg:pt-primary">
      <Container>
        {/*
          Mobile card wrapper — on lg+ these classes collapse to nothing, so
          the desktop layout reverts to a bare flex row exactly as before.
        */}
        <div
          className="
            bg-[#1f1f22] border border-white/[0.08] rounded-2xl p-5
            lg:bg-transparent lg:border-0 lg:rounded-none lg:p-0
          "
        >
          <div className="flex lg:items-center flex-col lg:flex-row gap-5 lg:gap-10">
            {/* Avatar — mobile: 80px, lg: 288px (unchanged). Primary ring mobile only. */}
            <div className="flex items-center gap-4 lg:block lg:w-72 lg:h-72">
              <div
                className="
                  w-20 h-20 rounded-full overflow-hidden flex-shrink-0
                  ring-2 ring-primary/30
                  lg:w-72 lg:h-72 lg:ring-0
                "
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={data?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-lightGrey flex items-center justify-center text-white font-bold text-2xl">
                    {data?.name?.charAt(0) || "I"}
                  </div>
                )}
              </div>
              {/* Mobile-only: name sits next to the avatar for a tighter header row */}
              <div className="flex-1 min-w-0 lg:hidden">
                <p className="text-white/60 text-xs uppercase tracking-[0.14em] font-semibold">
                  {t("courses.instructor") || "Instructor"}
                </p>
                <h3 className="text-white text-lg font-bold mt-1 truncate">
                  {data?.name}
                </h3>
              </div>
            </div>

            <div className="flex flex-col gap-2 lg:gap-4 flex-1">
              {/* Desktop-only name (mobile renders it beside the avatar above) */}
              <h3 className="hidden lg:block text-2xl font-bold text-white">
                {data?.name}
              </h3>

              <p className="text-white/80 text-sm leading-relaxed lg:text-white lg:text-lg lg:w-3/4">
                {data?.bio}
              </p>

              {/* Social links — mobile only (desktop layout unchanged) */}
              {socials.length > 0 && (
                <div className="flex items-center gap-2 mt-1 lg:hidden">
                  {socials.map(({ href, Icon, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="
                        w-9 h-9 rounded-lg
                        bg-white/[0.06] border border-white/[0.08]
                        flex items-center justify-center
                        text-white/80 hover:text-primary hover:border-primary/40
                        transition-colors
                      "
                    >
                      <Icon size={18} weight="bold" />
                    </a>
                  ))}
                </div>
              )}

              <Link
                to={`/instructor-profile/${data?.slug}`}
                className="
                  mt-4 lg:mt-0
                  w-full lg:w-max
                  bg-primary hover:bg-darkPrimary
                  text-white font-bold lg:font-normal
                  rounded-xl lg:rounded-md
                  px-6 py-3 lg:py-2
                  flex items-center justify-center gap-x-2
                  transition-colors
                  shadow-[0_6px_14px_rgba(237,26,77,0.28)] lg:shadow-none
                "
              >
                <span>{t("courses.view_profile")}</span>
                <ArrowRight
                  size={16}
                  className={`lg:hidden ${isRTL ? "rotate-180" : ""}`}
                />
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Instructor;
